import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isAuthenticated, signIn, type AuthError } from '../lib/auth';

/**
 * Sign-in screen — real email/password against /api/auth/signin.
 *
 * On success we honor `?next=<path>` (set by `api.ts` when a 401 bounce
 * happens) so the user lands back where they were. With no `next`, they go
 * to /dashboard.
 *
 * `?reset=1` (set after a successful password reset) just shows a one-line
 * success banner above the form — the user still has to sign in fresh.
 */
export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get('next') || '/dashboard';
  const justReset = params.get('reset') === '1';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, nextPath]);

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    const res = await signIn(email.trim(), password);
    setPending(false);
    if (res.ok) {
      navigate(nextPath, { replace: true });
      return;
    }
    setError(res.error);
  }

  return (
    <div className="min-h-screen relative grid place-items-center px-5 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0  opacity-80" />
      <div aria-hidden="true" className="absolute inset-x-0 top-1/4 h-[400px] " />

      <div className="relative w-full max-w-[420px]">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="w-8 h-8 rounded-md bg-ink text-paper grid place-items-center font-extrabold text-[15px] leading-none" aria-hidden="true">T</span>
          <span className="font-bold tracking-tight text-[20px] text-ink">Thren</span>
        </div>

        <div className="surface p-7">
          <h1 className="text-[24px] font-bold tracking-tight text-ink">Welcome back</h1>
          <p className="mt-1.5 text-[14px] text-muted leading-relaxed">
            Sign in to pick up where you left off.
          </p>

          {justReset && (
            <div
              role="status"
              className="mt-5 rounded-md border border-candy-lime/30 bg-candy-lime/10 text-candy-lime px-3 py-2 text-[12.5px]"
            >
              Password updated. Sign in with your new password.
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-5 space-y-3.5" noValidate>
            <Field
              id="email"
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={setEmail}
              disabled={pending}
            />
            <Field
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={setPassword}
              disabled={pending}
            />

            {error && (
              <p
                role="alert"
                className="text-[12.5px] text-candy-pink leading-relaxed"
              >
                {messageFor(error)}
              </p>
            )}

            <button
              type="submit"
              disabled={pending || !email || !password}
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pending ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="flex items-center justify-between pt-1 text-[12.5px]">
              <Link
                to="/forgot-password"
                className="text-accent hover:underline"
              >
                Forgot password?
              </Link>
              <span className="text-muted">
                Don&rsquo;t have an account?{' '}
                <Link to="/signup" className="text-accent hover:underline">
                  Sign up
                </Link>
              </span>
            </div>
          </form>
        </div>

        <p className="mt-4 text-center text-[11px] text-muted">
          By signing in you agree to the{' '}
          <a className="hover:text-muted" href="#">
            terms
          </a>{' '}
          and{' '}
          <a className="hover:text-muted" href="#">
            privacy policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function messageFor(err: AuthError): string {
  switch (err) {
    case 'invalid_credentials':
      return 'That email and password don’t match.';
    case 'network':
      return 'Could not reach the server. Check your connection and try again.';
    case 'email_taken':
    case 'invalid_email':
    case 'weak_password':
    default:
      return 'Something went wrong. Please try again.';
  }
}

interface FieldProps {
  id: string;
  label: string;
  type: 'email' | 'password' | 'text';
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  required?: boolean;
  disabled?: boolean;
}

function Field(p: FieldProps) {
  return (
    <label htmlFor={p.id} className="block">
      <span className="block text-[11.5px] uppercase tracking-wider text-muted font-semibold mb-1.5">
        {p.label}
      </span>
      <input
        id={p.id}
        type={p.type}
        autoComplete={p.autoComplete}
        required={p.required}
        disabled={p.disabled}
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
        className="w-full rounded-md bg-paper2 border border-rule px-3 py-2.5 text-[14px] text-ink placeholder:text-muted/45 outline-none transition-colors focus:border-electric-500/60 focus:bg-paper2 disabled:opacity-60"
      />
    </label>
  );
}
